import { Maybe } from "@figurepos/fgr-lib-business-logic/lib/common/types"
import * as React from "react"
import { Button, Checkbox, Form, Select } from "antd"
import {
    CheckboxFormItem,
    formItemLayoutBalanced,
    renderInputNumberFormItem,
} from "../../../_components/SettingFormItems"
import DrawerBlockComponent from "../../../../../uiKit/Drawer/components/DrawerBlockComponent"
import type { SettingFormItem } from "../../../types"
import { FormInputPriceValue } from "../../../_components/FormInputPriceValue"
import { DeliveryFutureOrders } from "./DeliveryFutureOrders"
import { WrappedFormUtils } from "antd/lib/form/Form"
import type { TimeSpan } from "@figurepos/fgr-lib-business-logic/lib/common/dateTime/hours_of_operation_v2"
import { HooWarning } from "../HooWarning"

type Props = {
    form: WrappedFormUtils
    settings: SettingFormItem
    deliveryEnabled: boolean
    defaultDeliveryTime: Maybe<number>
    acceptOnlyFutureOrderForDelivery: boolean
    isDeliveryFutureOrders: boolean
    isWebAndMobileOrderingEnabled: boolean
    leadTimeActiveDelivery: boolean
    futureDeliveryDays: Maybe<number>
    onStateValueChanged: (key: string, val: Maybe<any>) => void
    onChangeCallback: () => void
    deliveryHooActive: boolean
    deliveryHoo: TimeSpan[]
    onDeliveryHooModalOpen: () => void
    onDeliveryHooActiveToggle: (value: boolean) => void
}

export const DeliverySettingsForm = (props: Props) => {
    return (
        <DrawerBlockComponent
            title={props.isWebAndMobileOrderingEnabled ? "Delivery (MIB + Mobile)" : "Delivery"}
            showSwitch={true}
            onSwitchChange={(val: boolean) => props.onStateValueChanged("deliveryEnabled", val)}
            switchValue={props.deliveryEnabled}
            style={{
                padding: 0,
                position: "relative",
            }}>
            {!props.deliveryEnabled && (
                <div
                    style={{
                        position: "absolute",
                        width: "100%",
                        height: "100%",
                        backgroundColor: "#FFF",
                        zIndex: "4",
                        opacity: "0.6",
                    }}
                />
            )}
            <div>
                <div>
                    <Form.Item
                        style={{
                            marginTop: "0.25rem",
                            marginBottom: "0rem",
                            borderTop: "none",
                        }}
                        className="receipt-setting-form-item"
                        {...formItemLayoutBalanced}
                        label="Set Hours Of Operation for Delivery">
                        <div
                            style={{
                                position: "relative",
                                top: "5px",
                            }}>
                            <Button
                                disabled={!props.deliveryHooActive}
                                onClick={() => props.onDeliveryHooModalOpen()}
                                style={{
                                    marginRight: "1rem",
                                }}>
                                Modify
                            </Button>
                            <Checkbox
                                onChange={e => props.onDeliveryHooActiveToggle(e.target.checked)}
                                checked={props.deliveryHooActive}
                            />
                        </div>
                    </Form.Item>
                    <div
                        style={{
                            position: "relative",
                            bottom: "14px",
                            paddingLeft: "1rem",
                            lineHeight: "1.3",
                            fontSize: "12px",
                            color: "#7E7E7E",
                        }}>
                        If not active, Location Hours Of Operation are used as default
                    </div>
                    <HooWarning
                        hooIsActive={props.deliveryHooActive}
                        hooSpans={props.deliveryHoo}
                        message="No hours have been set. Delivery will appear as closed to customers until hours are added."
                    />
                </div>

                {CheckboxFormItem(
                    props.settings,
                    "MIB_ACCEPT_CARD_FOR_DELIVERY",
                    "Accept Online Card Payments",
                    props.form,
                    null,
                    props.onChangeCallback,
                    "receipt-setting-form-item",
                    "Customer will pay online, upon placing the order"
                )}
                {CheckboxFormItem(
                    props.settings,
                    "MIB_ACCEPT_CASH_FOR_DELIVERY",
                    "Accept Payments Upon Delivery",
                    props.form,
                    null,
                    props.onChangeCallback,
                    "receipt-setting-form-item",
                    "Customer will pay upon delivery, directly to the delivery person"
                )}
                {CheckboxFormItem(
                    props.settings,
                    "MIB_ACCEPT_TIPS_FOR_DELIVERY",
                    "Accept Tips with Online Card Payments",
                    props.form,
                    null,
                    props.onChangeCallback,
                    "receipt-setting-form-item"
                )}

                <div>
                    <Form.Item
                        style={{
                            marginBottom: "0rem",
                        }}
                        className="receipt-setting-form-item"
                        {...formItemLayoutBalanced}
                        label="Estimated Default Delivery Time">
                        <Select
                            defaultValue={props.defaultDeliveryTime}
                            value={props.defaultDeliveryTime}
                            onChange={value => props.onStateValueChanged("defaultDeliveryTime", value)}
                            style={{
                                width: "12rem",
                            }}>
                            {getDefaultDeliveryPickupTimeValues(props.defaultDeliveryTime)}
                        </Select>
                    </Form.Item>
                </div>

                <FormInputPriceValue
                    form={props.form}
                    data={{
                        key: "MIB_MIN_DELIVERY_VALUE",
                        ...props.settings.MIB_MIN_DELIVERY_VALUE,
                    }}
                    min={0}
                    label="Minimum Value of Order for Deliver ($)"
                    description={null}
                    placeholder="$10"
                    onChangeCallback={props.onChangeCallback}
                    className="receipt-setting-form-item"
                    customFormItemLayout={formItemLayoutBalanced}
                    style={{}}
                />

                {renderInputNumberFormItem(
                    props.settings,
                    "MIB_LAST_DELIVERY_TIME",
                    "Last Delivery Orders Before Closing Time (minutes)",
                    "",
                    props.form,
                    null,
                    props.onChangeCallback,
                    "receipt-setting-form-item"
                )}

                <DeliveryFutureOrders
                    form={props.form}
                    settings={props.settings}
                    acceptOnlyFutureOrderForDelivery={props.acceptOnlyFutureOrderForDelivery}
                    futureDeliveryDays={props.futureDeliveryDays}
                    isDeliveryFutureOrders={props.isDeliveryFutureOrders}
                    leadTimeActiveDelivery={props.leadTimeActiveDelivery}
                    onChangeCallback={props.onChangeCallback}
                    onStateValueChanged={props.onStateValueChanged}
                />

                {CheckboxFormItem(
                    props.settings,
                    "MIB_DELIVERY_CONTACT_FREE_OPTION_ENABLED",
                    "Enable option for 'Contact-Free Delivery'",
                    props.form,
                    null,
                    props.onChangeCallback,
                    "receipt-setting-form-item"
                )}
            </div>
        </DrawerBlockComponent>
    )
}

const getDefaultDeliveryPickupTimeValues = (currentValue: Maybe<number>) => {
    const minutes = [0, 10, 15, 20, 25, 30, 40, 50, 60]

    if (currentValue && !minutes.includes(currentValue)) {
        minutes.push(currentValue)
    }

    return minutes.map(v => {
        if (v === 0) {
            return (
                <Select.Option key={1} value={0}>
                    0 Minutes (Immediate)
                </Select.Option>
            )
        } else {
            return (
                <Select.Option key={2} value={v}>
                    {v} Minutes
                </Select.Option>
            )
        }
    })
}
